import React, { memo } from 'react';
import { TimeEntry } from '../types/database';
import { cn } from '../lib/utils';
import { getShiftTone } from '../lib/shift-colors';

interface CalendarDayProps {
  date: Date;
  entry?: TimeEntry;
  isToday: boolean;
  isSelected: boolean;
  isWeekend: boolean;
  onSelect: (date: Date) => void;
}

const MAX_VISIBLE_SHIFTS = 2;
const MAX_VISIBLE_SHIFTS_WIDE = 3;

export const CalendarDay: React.FC<CalendarDayProps> = memo(({ date, entry, isToday, isSelected, isWeekend, onSelect }) => {
  const shifts = entry?.shifts ?? [];
  const hasEntry = !!entry;
  const label = date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <button
      type="button"
      onClick={() => onSelect(date)}
      aria-pressed={isSelected}
      aria-label={hasEntry ? `${label}, ${entry.totalHours.toFixed(1)} hours logged` : `${label}, no hours logged`}
      className={cn(
        // Mobile: compact, centred cells. sm+: roomy cells with the date top-left and shift chips below.
        'group relative flex min-h-[3.75rem] flex-col items-center rounded-xl border p-1 text-center transition-[background-color,border-color,box-shadow] duration-150',
        'sm:min-h-[6.25rem] sm:items-stretch sm:p-2 sm:text-left 2xl:min-h-[8.75rem]',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand',
        hasEntry
          ? 'border-line bg-raised/80 hover:border-line-strong hover:bg-elevated'
          : cn('border-transparent hover:border-line hover:bg-white/[0.03]', isWeekend && 'sm:bg-white/[0.012]'),
        isSelected && 'border-brand/70 bg-brand/[0.08] shadow-glow hover:border-brand/70 hover:bg-brand/[0.1]'
      )}
    >
      <div className="flex items-start justify-center gap-1 sm:justify-between">
        <span
          className={cn(
            'numeric grid size-6 place-items-center rounded-full text-xs font-medium sm:size-7 sm:text-[13px] 2xl:size-8 2xl:text-sm',
            isToday
              ? 'bg-brand text-brand-fg shadow-[0_0_0_3px_rgb(155_109_255/0.2)]'
              : hasEntry
                ? 'text-fg'
                : 'text-fg-subtle group-hover:text-fg-muted'
          )}
        >
          {date.getDate()}
        </span>
        {hasEntry && (
          <span className="numeric hidden pt-1 text-xs font-medium text-fg-muted sm:inline 2xl:text-[13px]">
            {entry.totalHours.toFixed(1)}h
          </span>
        )}
      </div>

      {hasEntry && (
        <>
          {/* Desktop: shift chips */}
          <div className="mt-auto hidden flex-col gap-1 pt-1.5 sm:flex">
            {shifts.slice(0, MAX_VISIBLE_SHIFTS_WIDE).map((shift, index) => (
              <span
                key={index}
                title={`Shift ${index + 1}: ${shift.clockIn} – ${shift.clockOut || '…'} (${shift.hours?.toFixed(1) || '0.0'}h)`}
                className={cn(
                  'numeric flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-fg ring-1 ring-inset',
                  'lg:px-2 lg:py-1 lg:text-xs 2xl:text-[13px]',
                  getShiftTone(index).chip,
                  // The extra chip only fits in the taller cells on very wide screens
                  index >= MAX_VISIBLE_SHIFTS && 'hidden 2xl:flex'
                )}
              >
                <span className={cn('hidden size-1.5 shrink-0 rounded-full lg:block', getShiftTone(index).dot)} />
                <span className="truncate">{shift.clockIn}–{shift.clockOut || '…'}</span>
              </span>
            ))}
            {shifts.length > MAX_VISIBLE_SHIFTS && (
              <span className="px-1 text-[11px] text-fg-muted lg:text-xs 2xl:hidden">+{shifts.length - MAX_VISIBLE_SHIFTS} more</span>
            )}
            {shifts.length > MAX_VISIBLE_SHIFTS_WIDE && (
              <span className="hidden px-1 text-xs text-fg-muted 2xl:inline">+{shifts.length - MAX_VISIBLE_SHIFTS_WIDE} more</span>
            )}
          </div>

          {/* Mobile: hours + shift dots */}
          <div className="mt-auto flex flex-col items-center gap-1 pb-0.5 sm:hidden">
            <span className="numeric text-[11px] leading-none font-medium text-fg-muted">
              {Number(entry.totalHours.toFixed(1))}h
            </span>
            <div className="flex gap-0.5">
              {shifts.slice(0, 4).map((_, index) => (
                <span key={index} className={cn('size-1.5 rounded-full', getShiftTone(index).dot)} />
              ))}
            </div>
          </div>
        </>
      )}
    </button>
  );
});
